import { Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { ReactComponent as AllSprints } from 'assets/svgs/all-sprints.svg';
import { Menu } from 'components/atoms/dropdown';
import { RbacMenuItem } from 'components/organisms/rbac/components/rbac-menu';
import { useSprintsOptionsForProject } from 'data/projects/hooks/use-sprints';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { useTaskDetailQuery } from 'queries/task';

const StyledTooltip = styled(Tooltip)`
  .ant-tooltip {
    width: 120px;
  }
`;

const StyledMenuHeader = styled(Menu.Header)`
  padding-top: var(--spacing-05);
  div {
    display: flex;
    flex-direction: row;
  }
`;

const CurrentSprintWrapper = styled.div`
  padding: var(--spacing-05);
  height: 75px;
  border-bottom: 1px solid var(--border-color);
  & > :first-child {
    font-weight: var(--font-weight-bold);
    padding-bottom: var(--spacing-03);
  }

  & > :last-child {
    padding-bottom: var(--spacing-05);
  }
  cursor: default;
`;

interface Props {
  projectId: number;
  taskId: number;
}

export const AddToSprintMenu: FC<Props> = ({ projectId, taskId, ...rest }) => {
  const { data: sprints } = useSprintsOptionsForProject(ProjectId(projectId));
  const { data: taskData } = useTaskDetailQuery(TaskId(projectId, taskId));
  const { currentSprint } = taskData ?? {};

  const dispatch = useDispatch();

  const handleAddToSprint = (sprintId) => {
    dispatch(
      updateTaskSagaAction({
        projectId,
        taskId,
        data: {
          currentSprint: {
            sprintId,
          },
        },
      }),
    );
  };

  const sprintsWithoutCurrentSprint = sprints?.filter((sprint) => {
    return currentSprint?.sprintId !== sprint.sprintId;
  });

  if (sprintsWithoutCurrentSprint?.length === 0) {
    return (
      <RbacMenuItem {...rest} key="add-sprint" icon={<AllSprints />} disabled>
        <StyledTooltip
          title={<FormattedMessage id="sprint.no_active_sprints" />}
          placement="right"
        >
          <div>
            <FormattedMessage id="add_task_to_sprint" />
          </div>
        </StyledTooltip>
      </RbacMenuItem>
    );
  }

  return (
    <>
      <Menu.SubMenu
        {...rest} /* do not remove the rest, otherwise submenu won't work inside a Menu */
        key="add-sprint"
        icon={<AllSprints />}
        popupClassName="scrollable_submenu"
        title={
          <span data-cy="task_more_options.add_to_sprint">
            <FormattedMessage id="add_task_to_sprint" />
          </span>
        }
        disabled={sprintsWithoutCurrentSprint?.length === 0}
      >
        {currentSprint && (
          <CurrentSprintWrapper>
            <div>
              <FormattedMessage id="current_sprint" />
            </div>
            <div>{currentSprint?.sprintName}</div>
          </CurrentSprintWrapper>
        )}
        <StyledMenuHeader className="sticky_menu_header">
          <FormattedMessage id="choose_a_sprint" />
        </StyledMenuHeader>
        {sprintsWithoutCurrentSprint?.map((sprint) => (
          <Menu.Item
            key={sprint.sprintId}
            onClick={() => {
              handleAddToSprint(sprint.sprintId);
            }}
            title={sprint.sprintName}
          >
            {sprint.sprintName}
          </Menu.Item>
        ))}
      </Menu.SubMenu>
    </>
  );
};
